import { create } from "zustand";
import type { RunStatus, MdmRunStats } from "../types";

type StageData = {
  status: string;
  records_processed?: number;
  duration_ms?: number;
  error?: string;
};

// Status rank — higher rank wins; SSE events never get downgraded by poll.
const STATUS_RANK: Record<string, number> = {
  pending: 0,
  running: 1,
  complete: 2,
  failed: 2,
};

interface MdmRunState {
  runId: string | null;
  runName: string | null;
  runStatus: RunStatus | null;
  entityType: string | null;
  lastCompletedRunId: string | null;
  sourceCount: number;
  pendingReviewCount: number;
  stats: MdmRunStats | null;
  stages: Record<string, StageData>;
  setRun: (id: string, name: string) => void;
  setRunStatus: (status: RunStatus) => void;
  setEntityType: (entityType: string) => void;
  setSourceCount: (count: number) => void;
  setPendingReviewCount: (count: number) => void;
  setStats: (stats: MdmRunStats) => void;
  /** Called by SSE — always wins. */
  updateStage: (stage: string, data: StageData) => void;
  /** Called by poll — only advances, never downgrades a stage SSE already moved. */
  updateStageFromPoll: (stage: string, data: StageData) => void;
  reset: () => void;
}

export const useMdmRunStore = create<MdmRunState>((set) => ({
  runId: null,
  runName: null,
  runStatus: null,
  entityType: null,
  lastCompletedRunId: null,
  sourceCount: 0,
  pendingReviewCount: 0,
  stats: null,
  stages: {},
  setRun: (id, name) => set({ runId: id, runName: name }),
  setRunStatus: (status) =>
    set((state) => ({
      runStatus: status,
      lastCompletedRunId:
        status === "completed" && state.runId
          ? state.runId
          : state.lastCompletedRunId,
    })),
  setEntityType: (entityType) => set({ entityType }),
  setSourceCount: (count) => set({ sourceCount: count }),
  setPendingReviewCount: (count) => set({ pendingReviewCount: count }),
  setStats: (stats) => set({ stats }),
  updateStage: (stage, data) =>
    set((state) => ({ stages: { ...state.stages, [stage]: data } })),
  updateStageFromPoll: (stage, data) =>
    set((state) => {
      const existing = state.stages[stage];
      if (
        existing &&
        (STATUS_RANK[existing.status] ?? 0) >= (STATUS_RANK[data.status] ?? 0)
      ) {
        return state; // SSE already moved this stage further — don't regress
      }
      return { stages: { ...state.stages, [stage]: data } };
    }),
  reset: () =>
    set((state) => ({
      runId: null,
      runName: null,
      runStatus: null,
      entityType: null,
      sourceCount: 0,
      pendingReviewCount: 0,
      stats: null,
      stages: {},
      lastCompletedRunId: state.lastCompletedRunId,
    })),
}));
